import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import ImagemProduto from '@/components/feature/ImagemProduto';
import { formatarCentavos } from '@/pages/loja/utils';
import type { VitrineLoja, ProdutoVitrine } from '@/pages/fichas/types';

interface Props {
  eventoId: number;
  eventoNome: string;
  eventoImagem: string | null;
  slug: string;
}

// Cartão-chamariz na página do evento: convida a abrir a vitrine de fichas.
// Some se não há loja ou se o evento já terminou.
export default function ChamarizLoja({ eventoId, eventoNome, eventoImagem, slug }: Props) {
  const [vitrine, setVitrine] = useState<VitrineLoja | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    supabase
      .rpc('loja_vitrine', { p_evento: eventoId })
      .then(({ data, error }) => {
        if (!ativo) return;
        if (error) {
          setCarregando(false);
          return;
        }
        setVitrine((data as VitrineLoja) ?? null);
        setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const produtos = useMemo<ProdutoVitrine[]>(
    () =>
      (vitrine?.pontos ?? [])
        .flatMap((p) => p.produtos)
        .filter((p) => p.situacao !== 'esgotou'),
    [vitrine],
  );

  const destaques = useMemo(() => produtos.slice(0, 3), [produtos]);
  const menorPreco = useMemo(
    () => (produtos.length ? Math.min(...produtos.map((p) => p.preco_centavos)) : null),
    [produtos],
  );
  const nBarracas = (vitrine?.pontos ?? []).length;

  if (carregando) return null;
  if (!vitrine) return null;
  if (vitrine.estado === 'sem_loja' || vitrine.estado === 'inexistente') return null;
  if (vitrine.estado === 'encerrada') return null;
  // Só aparece quando as vendas estão abertas e existe pelo menos uma ficha à venda.
  if (vitrine.estado !== 'aberta') return null;
  if (!produtos.some((p) => p.situacao === 'a_venda')) return null;

  const resumoTexto =
    nBarracas > 0 && menorPreco != null
      ? `${nBarracas} ${nBarracas === 1 ? 'barraca' : 'barracas'} · a partir de ${formatarCentavos(menorPreco)}`
      : null;

  return (
    <section className="relative overflow-hidden">
      {eventoImagem ? (
        <img
          src={eventoImagem}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center"
          style={{ filter: 'blur(24px)', transform: 'scale(1.1)' }}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-500 to-primary-400" />
      )}
      <div className="absolute inset-0 bg-primary-900/60" />

      <div className="relative z-10 mx-auto w-full max-w-[720px] px-4 py-10 lg:py-14">
        <div className="rounded-3xl bg-white/85 p-5 shadow-xl backdrop-blur-md sm:p-6">
          <h2 className="text-xl font-bold text-foreground-950">Compre suas fichas</h2>

          {resumoTexto && (
            <p className="mt-1 text-sm text-foreground-600">{resumoTexto}</p>
          )}

          {destaques.length > 0 && (
            <div className="mt-5 grid grid-cols-3 gap-3">
              {destaques.map((p) => (
                <div key={p.produto_id} className="rounded-2xl bg-white/80 p-2">
                  <div className="overflow-hidden rounded-xl">
                    <ImagemProduto
                      imagemUrl={p.imagem_url}
                      imagemSlug={p.imagem_slug}
                      emoji={p.emoji}
                      nome={p.nome}
                      tamanho="grande"
                      lazy
                    />
                  </div>
                  <p className="mt-2 line-clamp-1 text-xs font-medium text-foreground-800">
                    {p.nome}
                  </p>
                  <p className="mt-0.5 text-xs font-bold text-primary-700">
                    {formatarCentavos(p.preco_centavos)}
                  </p>
                </div>
              ))}
            </div>
          )}

          <Link
            to={`/e/${slug}/fichas`}
            className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:from-primary-600 hover:to-primary-700"
          >
            Ver as fichas
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}